import { MOCK_PERSONAS } from "./Accountmockpersonas";

// Keeps a working (mutable) copy of the active persona's data, so actions
// like rename / invite / delete / accept feel real for the rest of the
// session, without touching the original fixtures. Resets whenever the
// persona is switched.
let workingState = null;
let workingPersonaId = null;

const getWorkingState = (personaId) => {
    if (workingPersonaId !== personaId) {
        workingState = JSON.parse(JSON.stringify(MOCK_PERSONAS[personaId]));
        workingPersonaId = personaId;
    }
    return workingState;
};

const delay = (ms = 250) => new Promise((resolve) => setTimeout(resolve, ms));

const notFound = (path) => {
    const err = new Error(`[mock] No mock route for ${path}`);
    err.status = 404;
    throw err;
};

export async function mockRequest(personaId, path, { method = "GET", body } = {}) {
    await delay();

    if (!MOCK_PERSONAS[personaId]) {
        throw new Error(`[mock] Unknown persona "${personaId}"`);
    }
    const state = getWorkingState(personaId);

    // ---- Auth / current user ----
    if (path === "/me" && method === "GET") return state.user;

    // ---- Plan ----
    if (path === "/me/plan" && method === "GET") return state.plan;

    if (/^\/organizations\/[^/]+\/rename$/.test(path) && method === "POST") {
        state.plan.organizationName = body.name;
        state.user.organization.name = body.name;
        return { success: true, organization: { id: state.user.organization.id, name: body.name } };
    }

    if (path === "/me/api-key/reveal" && method === "POST") {
        return { success: true, apiKey: "rf_live_sk_test_51Mx8f2c9aB3dE7gH" };
    }

    if (path === "/me/api-key/regenerate" && method === "POST") {
        const newMasked = `rf_live_••••••••••••${Math.random().toString(16).slice(2, 6)}`;
        state.plan.apiKey = { ...state.plan.apiKey, masked: newMasked };
        return { success: true, apiKey: { masked: newMasked, full: `rf_live_sk_test_${Date.now()}` } };
    }

    // ---- Workspaces ----
    if (/^\/organizations\/[^/]+\/workspaces$/.test(path) && method === "GET") {
        return state.workspaces;
    }

    if (path === "/access-requests" && method === "POST") {
        return {
            success: true,
            request: { id: `req_${Date.now()}`, workspaceId: body.workspaceId, requestedRole: body.role, status: "pending" },
        };
    }

    const acceptMatch = path.match(/^\/access-requests\/([^/]+)\/accept$/);
    if (acceptMatch && method === "POST") {
        const requestId = acceptMatch[1];
        const notification = state.notifications.items.find(
            (n) => n.payload?.requestId === requestId
        );
        if (notification) {
            if (!notification.isRead) {
                state.notifications.unreadCount = Math.max(0, state.notifications.unreadCount - 1);
            }
            notification.isRead = true;
            notification.isActionable = false; // pulls it out of "Pending Access Requests"
        }
        return { success: true, message: "Access request accepted" };
    }

    const declineMatch = path.match(/^\/access-requests\/([^/]+)\/decline$/);
    if (declineMatch && method === "POST") {
        const requestId = declineMatch[1];
        const notification = state.notifications.items.find(
            (n) => n.payload?.requestId === requestId
        );
        if (notification) {
            if (!notification.isRead) {
                state.notifications.unreadCount = Math.max(0, state.notifications.unreadCount - 1);
            }
            notification.isRead = true;
            notification.isActionable = false;
        }
        return { success: true, message: "Access request declined" };
    }

    // ---- Team / members ----
    const membersMatch = path.match(/^\/organizations\/([^/]+)\/members(?:\/([^/]+))?$/);
    if (membersMatch && state.members) {
        const memberId = membersMatch[2];

        if (!memberId && method === "GET") return state.members;

        if (!memberId && method === "POST") {
            const newMember = {
                id: `mem_${Date.now()}`, userId: `user_${Date.now()}`, name: body.name, email: body.email,
                avatar: null, isYou: false, aiCreditLimit: 0, status: "pending", roles: {},
            };
            state.members.members.push(newMember);
            state.members.membersCount += 1;
            return { success: true, member: newMember };
        }

        if (memberId && method === "PATCH") {
            const member = state.members.members.find((m) => m.id === memberId);
            if (!member) return notFound(path);
            if (body.aiCreditLimit !== undefined) member.aiCreditLimit = body.aiCreditLimit;
            if (body.roles) member.roles = { ...member.roles, ...body.roles };
            return { success: true, member };
        }

        if (memberId && method === "DELETE") {
            state.members.members = state.members.members.filter((m) => m.id !== memberId);
            state.members.membersCount = state.members.members.length;
            return { success: true, message: "Member removed" };
        }
    }

    // ---- Notifications ----
    if (path === "/me/notifications" && method === "GET") return state.notifications;

    const readMatch = path.match(/^\/me\/notifications\/([^/]+)\/read$/);
    if (readMatch && method === "POST") {
        const n = state.notifications.items.find((item) => item.id === readMatch[1]);
        if (n && !n.isRead) {
            n.isRead = true;
            state.notifications.unreadCount = Math.max(0, state.notifications.unreadCount - 1);
        }
        return { success: true };
    }

    if (path === "/me/notifications/unread-count" && method === "GET") {
        return { count: state.notifications.unreadCount };
    }

    // ---- Modules ----
    if (path === "/me/modules" && method === "GET") return state.modules;

    // ---- Billing ----
    if (path === "/me/invoices" && method === "GET") return state.invoices;

    return notFound(path);
}

export default mockRequest;